
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Heart, Clock, Gift, UserCheck, Sparkles, Copy, Check, Music, Camera, Play, Pause, Store, X } from 'lucide-react';
import Countdown from './components/Countdown';
import OpeningCard from './components/OpeningCard';
import LeafFall from './components/LeafFall';
import { NAMES, QUOTE, BANK_ACCOUNTS, GOOGLE_MAPS_URL, WHATSAPP_NUMBER, IMAGES, SONG_DETAILS, MUSIC_URL } from './constants';

const FloralOrnament = () => (
  <div className="flex items-center justify-center mb-6 opacity-60">
    <svg width="80" height="40" viewBox="0 0 100 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-olive">
      <path d="M50 5C50 5 55 15 45 25C35 35 20 30 20 30M50 5C50 5 45 15 55 25C65 35 80 30 80 30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="50" cy="5" r="2" fill="currentColor"/>
      <path d="M50 45V25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="4 4"/>
      <path d="M35 15C35 15 32 10 28 12C24 14 26 18 30 18" stroke="currentColor" strokeWidth="1"/>
      <path d="M65 15C65 15 68 10 72 12C76 14 74 18 70 18" stroke="currentColor" strokeWidth="1"/>
    </svg>
  </div>
);

const App: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSongPlaying, setIsSongPlaying] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-4');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
    setIsSongPlaying(false);
  };

  const toggleSong = () => {
    if (audioRef.current) {
      if (isSongPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => console.log("Play error", e));
      }
      setIsSongPlaying(!isSongPlaying);
    }
  };

  if (!isOpen) {
    return <OpeningCard onOpen={handleOpen} />;
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center py-8 sm:py-16 bg-white overflow-visible">
      <LeafFall />
      {/* Contenedor principal con márgenes laterales y textura relieve */}
      <div className="w-[92%] max-w-md relative shadow-[0_20px_50px_rgba(0,0,0,0.1)] bg-cream animate-fadeIn texture-paper texture-relief border border-gray-100 rounded-sm overflow-visible">
        <audio ref={audioRef} src={MUSIC_URL} loop />

        {/* Hero Section */}
        <section className="relative pt-16 pb-12 flex flex-col items-center justify-center text-center p-8 overflow-hidden">
          <div className="relative z-20 text-olive flex flex-col items-center">
            {/* Adorno Floral */}
            <FloralOrnament />
            
            <h1 className="font-script text-7xl mb-6 text-olive/90">
              {NAMES.groom.split(' ')[0]} <br />
              <span className="text-4xl block my-2">&</span>
              {NAMES.bride.split(' ')[0]}
            </h1>
            <p className="text-sm tracking-[0.5em] uppercase font-semibold text-olive/60 mb-8">
              18.04.2026
            </p>
            <div className="max-w-[300px] mb-12">
              <p className="italic font-serif text-xl leading-relaxed text-olive/80">
                "{QUOTE}"
              </p>
            </div>
          </div>
        </section>

        {/* Imagen Hero */}
        <div className="px-8 mb-24 reveal transition-all duration-1000 opacity-0 translate-y-4">
          <div 
            className="rounded-[60px] overflow-hidden shadow-2xl border-8 border-white rotate-2 cursor-zoom-in"
            onClick={() => setSelectedImage(IMAGES.hero)}
          >
              <img 
                src={IMAGES.hero} 
                alt="Samuel & Crismeri" 
                className="w-full aspect-square object-cover"
                referrerPolicy="no-referrer"
              />
          </div>
        </div>

        {/* Conteo Regresivo */}
        <section className="bg-olive text-white px-8 py-20 text-center reveal transition-all duration-1000 opacity-0 translate-y-4 shadow-xl texture-paper">
          <div className="absolute inset-0 opacity-10 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/pinstriped-suit.png')]"></div>
          <div className="relative z-10 flex flex-col items-center">
              <h2 className="text-sm uppercase tracking-[0.1em] mb-10 opacity-70">Con la bendición de Dios y nuestros padres tenemos el honor de invitarte a nuestra boda</h2>
              <div className="mb-12 flex flex-col items-center">
                  <span className="text-sm tracking-[0.4em] uppercase opacity-70 font-medium mb-3">Abril</span>
                  <span className="font-serif text-7xl font-light leading-none">18</span>
                  <span className="text-sm tracking-[0.4em] uppercase opacity-70 mt-4 font-medium">2026</span>
              </div>
             {/* <div className="w-16 h-px bg-white/20 mb-12"></div> */}
              <div className="w-full h-px bg-white/10 mt-6 mb-4" />
              <p className="text-[10px] uppercase tracking-[0.4em] opacity-70">Tiempo Restante</p>
              <Countdown />  
          </div>
        </section>

        {/* Imagen Intermedia */}
        <div className="px-8 mb-12 mt-12 reveal transition-all duration-1000 opacity-0 translate-y-4">
          <div 
            className="rounded-[60px] overflow-hidden shadow-2xl border-8 border-white -rotate-2 cursor-zoom-in"
            onClick={() => setSelectedImage("http://mediaexperto.com/temp/recursos/6.jpg")}
          >
              <img 
                src="http://mediaexperto.com/temp/recursos/6.jpg" 
                alt="Samuel & Crismeri Moment" 
                className="w-full aspect-[4/5] object-cover"
                referrerPolicy="no-referrer"
              />
          </div>
        </div>

        {/* Sección Nuestra Canción */}
        <section className="px-10 py-24 text-center reveal transition-all duration-1000 opacity-0 translate-y-4">
          <div className="flex justify-center mb-8 text-olive/30">
            <Music size={36} strokeWidth={1} />
          </div>
          <h2 className="font-serif text-4xl text-olive mb-6">Nuestra Canción</h2>
          <p className="text-gray-500 text-sm italic mb-12 tracking-wide">"Se van las horas - Musiko"</p>
          
          <div className="bg-white/70 backdrop-blur-md px-8 py-4 rounded-[3rem] border border-olive/10 shadow-lg inline-block w-full texture-paper">
             <div className="flex items-center gap-8 text-left">
                <button 
                  onClick={toggleSong}
                  className="w-16 h-16 rounded-full bg-olive flex items-center justify-center text-white shadow-xl active:scale-95 transition-all"
                >
                  {isSongPlaying ? <Pause size={30} fill="white" /> : <Play size={30} fill="white" className="ml-1" />}
                </button>
                <div className="flex-1">
                  <div className="flex justify-between items-end mb-3">
                     <span className="text-xs font-bold text-olive uppercase tracking-[0.2em]">
                       {isSongPlaying ? 'Escuchando' : 'Pausado'}
                     </span>
                     <div className="flex gap-1 items-end h-5">
                        {[1,2,3,4,5].map(i => (
                          <div key={i} className={`w-0.5 bg-olive/40 rounded-full ${isSongPlaying ? 'animate-pulse' : ''}`} style={{height: `${Math.random()*100}%`, animationDelay: `${i*0.1}s`}}></div>
                        ))}
                     </div>
                  </div>
                  <div className="h-1.5 w-full bg-olive/10 rounded-full overflow-hidden">
                    <div className={`h-full bg-olive transition-all duration-1000 ${isSongPlaying ? 'w-full' : 'w-0'}`}></div>
                  </div>
                </div>
             </div>
          </div>
        </section>

        {/* Detalles del Evento */}
        <section className="px-10 py-24 text-center reveal transition-all duration-1000 opacity-0 translate-y-4 bg-white/40 shadow-sm border-y border-olive/5 texture-paper">
          <div className="flex justify-center mb-8 text-olive/30">
            <Store size={36} strokeWidth={1} />
          </div>
          <h2 className="font-serif text-4xl text-olive mb-20">Información</h2>
          <div className="flex flex-col items-center gap-10">
            <div className="space-y-6">
              <div className="flex justify-center mb-8">
                <div className="p-5 rounded-full bg-white border border-olive/10 text-olive shadow-md">
                  <Heart size={36} strokeWidth={1} />
                </div>
              </div>
              <h2 className="font-serif text-3xl text-olive italic tracking-tight opacity-90">Ceremonia</h2>
              <div className="flex flex-col gap-3 items-center">
                <span className="flex items-center gap-3 text-base font-semibold tracking-[0.2em] text-olive/80 uppercase">
                  <Clock size={20} /> 3:30 PM
                </span>
                <p className="text-gray-600 text-base italic leading-relaxed max-w-[260px]">
                  Jardín del Seminario Menor <br/> San Pedro Apóstol
                </p>
              </div>
            </div>

            <div className="w-24 h-px bg-olive/10" />

            <div className="space-y-6">
              <div className="flex justify-center mb-8">
                <div className="p-5 rounded-full bg-white border border-olive/10 text-olive shadow-md">
                  <Sparkles size={36} strokeWidth={1} />
                </div>
              </div>
              <h2 className="font-serif text-3xl text-olive italic tracking-tight opacity-90">Recepción</h2>
              <div className="flex flex-col gap-3 items-center">
                <span className="flex items-center gap-3 text-base font-semibold tracking-[0.2em] text-olive/80 uppercase">
                  <Clock size={20} /> 5:30 PM
                </span>
                <p className="text-gray-600 text-base italic leading-relaxed max-w-[260px]">
                  Salón de Eventos <br/> Seminario Menor
                </p>
              </div>
            </div>
          </div>

          <div className="mt-20">
            <a 
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-4 bg-olive text-white px-12 py-6 rounded-full hover:shadow-2xl transition-all uppercase text-xs tracking-[0.3em] font-bold shadow-xl"
            >
              <MapPin size={20} /> Ver ubicación
            </a>
          </div>
        </section>

        {/* Galería */}
        <section className="px-8 py-24 text-center reveal transition-all duration-1000 opacity-0 translate-y-4">
          <div className="flex justify-center mb-8 text-olive/20">
            <Camera size={40} strokeWidth={1} />
          </div>
          <h2 className="font-serif text-4xl text-olive mb-16 tracking-tight">Nuestra Galería</h2>
          <div className="grid grid-cols-2 gap-4">
            {IMAGES.gallery.map((img, i) => (
              <div 
                key={i} 
                className={`rounded-[2rem] overflow-hidden shadow-lg border-2 border-white transform transition-transform duration-500 hover:scale-[1.03] cursor-zoom-in
                  ${i === 1000 ? 'col-span-2 h-80' : 'h-64'}`}
                onClick={() => setSelectedImage(img)}
              >
                <img 
                  src={img} 
                  alt={`Galería ${i}`} 
                  className="w-full h-full object-cover" 
                  referrerPolicy="no-referrer"
                />
              </div>
            ))}
          </div>
        </section>

        {/* Código de Vestimenta */}
        <section className="bg-olive text-white px-10 py-24 text-center reveal transition-all duration-1000 opacity-0 translate-y-4 shadow-inner texture-paper">
          <div className="absolute inset-0 opacity-10 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/pinstriped-suit.png')]"></div>
          <div className="max-w-xs mx-auto space-y-10 relative z-10">
            <h2 className="font-serif text-4xl text-white/90">Código de Vestimenta</h2>
            <div className="flex justify-center gap-12 py-6">
              <div className="flex flex-col items-center gap-4">
                <div className="bg-white/10 p-5 rounded-full"><img src="https://img.icons8.com/ios/100/FFFFFF/wedding-dress.png" className="w-14 opacity-80" alt="Damas" referrerPolicy="no-referrer" /></div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-white/60">Damas</span>
              </div>
              <div className="flex flex-col items-center gap-4">
                <div className="bg-white/10 p-5 rounded-full"><img src="https://img.icons8.com/ios/100/FFFFFF/suit.png" className="w-14 opacity-80" alt="Caballeros" referrerPolicy="no-referrer" /></div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-white/60">Caballeros</span>
              </div>
            </div>
            <p className="text-white font-serif text-3xl italic tracking-tight opacity-90">Elegante / Vintage</p>
            <div className="p-8 border border-white/20 rounded-[2rem] bg-white/5 shadow-inner">
              <p className="text-base leading-relaxed text-white/80 font-light italic">
                "Rogamos evitar los colores <span className="font-bold text-white">Blanco y Beige</span>, reservados para los novios."
                <br />
                <span className="text-[11px] mt-6 block opacity-40 font-semibold tracking-[0.3em] uppercase">(Celebración al aire libre)</span>
              </p>
            </div>
          </div>
        </section>

        {/* Regalos */}
        <section className="px-10 py-24 text-center reveal transition-all duration-1000 opacity-0 translate-y-4">
          <div className="flex justify-center mb-8 text-olive/40">
            <Gift size={48} strokeWidth={0.5} />
          </div>
          <h2 className="font-serif text-4xl text-olive mb-8 tracking-tight">Regalos</h2>
          <p className="text-gray-500 text-base mb-16 italic leading-relaxed px-4 font-light">
            "Para nosotros es un honor su presencia, si desea hacernos un regalo aquí le dejamos la opción que le plazca:"
          </p>

          <div className="space-y-8">
            {BANK_ACCOUNTS.map((bank, idx) => (
              <div key={idx} className="bg-white/80 border border-olive/5 p-6 rounded-[2.5rem] shadow-sm text-left relative overflow-hidden group hover:shadow-xl transition-all texture-paper">
                <div className="absolute top-0 left-0 w-2 h-full bg-olive/10"></div>
                <p className="text-[10px] uppercase tracking-[0.4em] text-olive font-bold mb-2 opacity-70">{bank.bank}</p>
                <h3 className="font-serif text-xl text-gray-800 mb-1">{bank.owner}</h3>
                <div className="flex items-center justify-between bg-olive/5 px-4 py-1 rounded-2xl">
                  <span className="text-olive/80 font-mono tracking-widest text-base">{bank.account}</span>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(bank.account);
                      setCopiedId(idx);
                      setTimeout(() => setCopiedId(null), 2000);
                    }}
                    className="p-3 text-olive hover:scale-125 transition-transform relative"
                  >
                    {copiedId === idx ? (
                      <>
                        <Check size={20} className="text-emerald-600" />
                        <span className="absolute -top-10 left-1/2 -translate-x-1/2 bg-olive text-white text-[10px] px-3 py-1.5 rounded-full shadow-lg animate-in fade-in slide-in-from-bottom-2 whitespace-nowrap font-bold uppercase tracking-widest">
                          ¡Copiado!
                        </span>
                      </>
                    ) : (
                      <Copy size={20} />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recomendaciones */}
        <section className="bg-olive text-white px-10 py-24 text-center reveal transition-all duration-1000 opacity-0 translate-y-4 rounded-t-[5rem] shadow-2xl texture-paper">
          <div className="absolute inset-0 opacity-10 pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/pinstriped-suit.png')]"></div>
          <div className="space-y-20 relative z-10">
            <div className="space-y-8">
              <h2 className="text-[11px] uppercase tracking-[0.6em] mb-6 opacity-40">Importante</h2>
              <div className="flex flex-col items-center gap-6">
                <UserCheck size={40} strokeWidth={1} className="opacity-60" />
                <p className="text-base font-light leading-relaxed px-8 italic opacity-80">Por favor, siga las indicaciones del personal del evento.</p>
              </div>
            </div>

            <div className="w-20 h-px bg-white/10 mx-auto" />

            <div className="space-y-8">
              <h2 className="font-serif text-4xl italic tracking-tight">Sin niños</h2>
              <p className="text-base font-light opacity-80 max-w-[280px] mx-auto leading-relaxed italic">
                "Amamos a sus niños, pero nuestro evento es solo para adultos."
              </p>
            </div>
          </div>
        </section>

        {/* Confirmación */}
        <section className="px-10 py-28 text-center reveal transition-all duration-1000 opacity-0 translate-y-4 bg-white/30 texture-paper">
          <h2 className="font-serif text-5xl text-olive mb-8 tracking-tight">Confirmar</h2>
          <p className="text-gray-500 mb-16 text-base italic font-light">
            Confirma tu asistencia antes del <br/> <strong className="text-olive font-semibold">01 de abril de 2026</strong>.
          </p>
          
          <a 
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hola! Confirmo mi asistencia a la boda de Samuel y Crismeri`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-5 bg-olive text-white px-8 mb-4 py-6 rounded-full hover:shadow-2xl transition-all uppercase text-xs tracking-[0.4em] font-bold shadow-xl active:scale-95"
          >
            Confirmar con la novia
          </a>

          <a 
            href={`https://wa.me/8295280180?text=Hola! Confirmo mi asistencia a la boda de Samuel y Crismeri`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-5 bg-olive text-white px-8 py-6 rounded-full hover:shadow-2xl transition-all uppercase text-xs tracking-[0.4em] font-bold shadow-xl active:scale-95"
          >
            Confirmar con el novio
          </a>

          <div className="mt-32">
            <p className="font-script text-4xl text-olive/30 mb-4">¡Los esperamos!</p>
            <div className="w-16 h-px bg-olive/10 mx-auto mt-8"></div>
          </div>
        </section>

        {/* Control de Música */}
        <div className="fixed bottom-10 right-10 z-[60]">
           <button 
             onClick={toggleSong}
             className="w-14 h-14 rounded-full bg-white/95 backdrop-blur-md border border-olive/10 flex items-center justify-center text-olive shadow-2xl active:scale-90 transition-all"
           >
             {isSongPlaying ? <Pause size={22} /> : <Music size={22} className="animate-bounce" />}
           </button>
        </div>
      </div>

      {/* Lightbox / Fullscreen Image Viewer */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors p-2"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedImage(null);
            }}
          >
            <X size={32} />
          </button>
          <img 
            src={selectedImage} 
            alt="Fullscreen" 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
            referrerPolicy="no-referrer"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default App;
